import { createSlice } from "@reduxjs/toolkit";
import configurationsInitial from "./mockups/configurationsMockup";

const DEFAULT_NEW_CONSTANTS = {
  BASE_PRICE: { label: "Bazinė kaina", value: 0 },
  PRICE_PER_CM: { label: "Bėginio cm kaina", value: 0 },
  BASE_WIDTH: { label: "Bazinis plotis", value: 0 },
};

const AVAILABLE_PARAMETERS = [
  { id: "WIDTH", label: "Plotis" },
  { id: "COLOR", label: "Spalva" },
];

const makeId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const configurationsSlice = createSlice({
  name: "configurations",
  initialState: {
    list: configurationsInitial,
    colorSuffixes: {},
    colorMappings: {},
    defaultVariations: {},
    wizardParameters: {},
    wizardSelections: {},
    constants: {
      "111-111": {
        BASE_PRICE: { label: "Bazinė kaina", value: 860 },
        PRICE_PER_CM: { label: "Bėginio cm kaina", value: 4 },
        BASE_WIDTH: { label: "Bazinis plotis", value: 100 },
      },
      "222-222": {
        BASE_PRICE: { label: "Bazinė kaina", value: 720 },
        PRICE_PER_CM: { label: "Bėginio cm kaina", value: 0.5 },
        BASE_WIDTH: { label: "Bazinis plotis", value: 210 },
      },
    },
  },
  reducers: {
    addConfiguration: (state, action) => {
      state.list.push(action.payload);
      state.constants[action.payload.id] = { ...DEFAULT_NEW_CONSTANTS };
    },
    removeConfiguration: (state, action) => {
      state.list = state.list.filter((c) => c.id !== action.payload);
      delete state.constants[action.payload];
      delete state.wizardParameters[action.payload];
      delete state.wizardSelections[action.payload];
    },
    updateConstant: (state, action) => {
      const { configurationId, constantId, value } = action.payload;
      if (!state.constants[configurationId]) {
        state.constants[configurationId] = { ...DEFAULT_NEW_CONSTANTS };
      }
      const existing = state.constants[configurationId][constantId];
      if (existing) {
        existing.value = value;
      }
    },
    addColorSuffix: (state, action) => {
      const { configurationId, colorLabel, suffix } = action.payload;
      if (!state.colorSuffixes[configurationId]) {
        state.colorSuffixes[configurationId] = {};
      }
      state.colorSuffixes[configurationId][colorLabel] = suffix;
    },
    setColorMapping: (state, action) => {
      const { configurationId, colorLabel, elementIds } = action.payload;
      if (!state.colorMappings[configurationId]) {
        state.colorMappings[configurationId] = {};
      }
      state.colorMappings[configurationId][colorLabel] = elementIds;
    },
    setDefaultVariation: (state, action) => {
      const { configurationId, variationId } = action.payload;
      state.defaultVariations[configurationId] = variationId;
    },
    addWizardParameter: (state, action) => {
      const { configurationId, label, paramType } = action.payload;
      if (!state.wizardParameters[configurationId]) {
        state.wizardParameters[configurationId] = [];
      }
      state.wizardParameters[configurationId].push({
        id: `WIZARD_${makeId()}`,
        label,
        type: paramType || "WIZARD",
        steps: [],
      });
    },
    removeWizardParameter: (state, action) => {
      const { configurationId, wizardId } = action.payload;
      const list = state.wizardParameters[configurationId];
      if (list) {
        state.wizardParameters[configurationId] = list.filter(
          (w) => w.id !== wizardId,
        );
      }
      // clean up selections
      const selections = state.wizardSelections[configurationId];
      if (selections) {
        const wizard = list?.find((w) => w.id === wizardId);
        if (wizard) {
          wizard.steps.forEach((s) => {
            delete selections[s.id];
          });
        }
      }
    },
    updateWizardParameter: (state, action) => {
      const { configurationId, wizardId, label, steps } = action.payload;
      const list = state.wizardParameters[configurationId];
      if (!list) return;
      const wizard = list.find((w) => w.id === wizardId);
      if (!wizard) return;
      if (label != null) wizard.label = label;
      if (steps != null) wizard.steps = steps;
    },
    setWizardStepSelection: (state, action) => {
      const { configurationId, stepId, value } = action.payload;
      if (!state.wizardSelections[configurationId]) {
        state.wizardSelections[configurationId] = {};
      }
      state.wizardSelections[configurationId][stepId] = value;
    },
    toggleParameter: (state, action) => {
      const { configurationId, parameterId } = action.payload;
      const config = state.list.find((c) => c.id === configurationId);
      if (!config) return;

      const paramDef = AVAILABLE_PARAMETERS.find((p) => p.id === parameterId);
      if (!paramDef) return;

      const idx = config.parameters.indexOf(paramDef.label);
      if (idx > -1) {
        config.parameters.splice(idx, 1);
      } else {
        config.parameters.push(paramDef.label);
        const order = AVAILABLE_PARAMETERS.map((p) => p.label);
        config.parameters.sort(
          (a, b) => order.indexOf(a) - order.indexOf(b),
        );
      }
    },
  },
});

export const {
  addConfiguration,
  removeConfiguration,
  updateConstant,
  addColorSuffix,
  setColorMapping,
  setDefaultVariation,
  addWizardParameter,
  removeWizardParameter,
  updateWizardParameter,
  setWizardStepSelection,
  toggleParameter,
} = configurationsSlice.actions;

export const selectConfigurations = (state) => state.configurations.list;
export const selectConstants = (configurationId) => (state) =>
  state.configurations.constants[configurationId] || DEFAULT_NEW_CONSTANTS;
export const selectColorSuffixes = (configurationId) => (state) =>
  state.configurations.colorSuffixes[configurationId] || {};
export const selectColorMappings = (configurationId) => (state) =>
  state.configurations.colorMappings[configurationId] || {};
export const selectDefaultVariation = (configurationId) => (state) =>
  state.configurations.defaultVariations[configurationId] || null;
export const selectWizardParameters = (configurationId) => (state) =>
  state.configurations.wizardParameters[configurationId] || [];
export const selectWizardSelections = (configurationId) => (state) =>
  state.configurations.wizardSelections[configurationId] || {};
export { AVAILABLE_PARAMETERS };

export default configurationsSlice.reducer;
